import { ref, computed, watch, unref, nextTick } from 'vue';

// Item type filters: issues vs processes ("Type" = 'IssueTemplate' marks issues)
const ITEM_TYPES = ['issues', 'processes'];

// Filter registration ids. Each keys both a context's filter registry and a
// DuckDB temp view (`filter_view_<id>`); renaming one orphans its view and
// silently loses the filter, so all four stay byte-identical (R11).
const STATUS_ITEMS_ID = 'statusFilter';
const STATUS_HISTORY_ID = 'statusHistoryFilter';
const TYPE_ITEMS_ID = 'itemTypeFilter';
const TYPE_HISTORY_ID = 'itemTypeHistoryFilter';

/**
 * The global status / item-type filter engine behind the filter FAB.
 *
 * It is the only thing in the app that writes BOTH filter contexts, and every
 * branch must stay atomic across the pair: all selected -> removeFilter on
 * both; none selected -> updateFilter([]) on both; partial -> two DIFFERENT
 * queries (status_history_temporal has no Type column, so the type filter has
 * to join back through items).
 *
 * Store-free, so this is the seed for a future createGlobalFilters() in
 * @rdrive/data-filters.
 *
 * @param {object} deps
 * @param {object|import('vue').Ref} deps.conn
 * @param {boolean|import('vue').Ref} deps.dataLoaded
 * @param {boolean|import('vue').Ref} [deps.isInitialized]  DuckDB ready flag;
 *   the original gated applyFilters on this as well as dataLoaded
 * @param {{ items: object, statusHistory: object }} deps.contexts  each a ref or
 *   a plain filter context; duck type { filters, filtersLastUpdated,
 *   updateFilter, updateFilterWithQuery, removeFilter }
 * @param {import('vue').WritableComputedRef<Object>} deps.statusFilters
 * @param {import('vue').WritableComputedRef<Object>} deps.itemTypeFilters
 */
export function useStatusTypeFilters({
  conn,
  dataLoaded,
  isInitialized = true,
  contexts,
  statusFilters,
  itemTypeFilters
}) {
  const itemsCtx = () => unref(contexts.items);
  const historyCtx = () => unref(contexts.statusHistory);

  // Local copies, replaced wholesale on every change — the original kept these
  // separate from the store-backed maps "for better reactivity with object
  // replacement", and the split is preserved because toggleAll relies on the
  // local copy being updated before nextTick()/applyFilters run.
  const localFilters = ref({});
  const localTypeFilters = ref({});

  // Sync local filters with the source of truth
  watch(() => statusFilters.value, (newFilters) => {
    if (newFilters && Object.keys(newFilters).length > 0) {
      localFilters.value = { ...newFilters };
    }
  }, { immediate: true, deep: true });

  // Seed the item types the first time round
  if (Object.keys(itemTypeFilters.value).length === 0) {
    itemTypeFilters.value = { issues: true, processes: true };
  }

  watch(() => itemTypeFilters.value, (newFilters) => {
    if (newFilters && Object.keys(newFilters).length > 0) {
      localTypeFilters.value = { ...newFilters };
    }
  }, { immediate: true, deep: true });

  // Status templates loaded from DuckDB, restricted to statuses actually in use,
  // with flags for which item types (issues/processes) use each status
  const allStatusTemplates = ref([]);

  const reload = async () => {
    const connection = unref(conn);
    if (!connection) return;
    try {
      const result = await connection.query(`
      SELECT st.Title AS "title", st.Color AS "color", st.Sort AS "sort", st.IsDraft AS "isDraft",
             MAX(CASE WHEN i."Type" = 'IssueTemplate' THEN 1 ELSE 0 END) AS "usedByIssues",
             MAX(CASE WHEN COALESCE(i."Type", '') != 'IssueTemplate' THEN 1 ELSE 0 END) AS "usedByProcesses"
      FROM status_templates st
      INNER JOIN items i ON st.Id = i.StatusId
      GROUP BY st.Title, st.Color, st.Sort, st.IsDraft
    `);
      const uniqueStatuses = new Map();
      result.toArray()
        .map(row => row.toJSON())
        .filter(status => status && status.title)
        .sort((a, b) => {
          const sortDiff = Number(a.sort || 0) - Number(b.sort || 0);
          if (sortDiff !== 0) return sortDiff;
          return String(b.color || '').localeCompare(String(a.color || ''));
        })
        .forEach(status => {
          const existing = uniqueStatuses.get(status.title);
          if (existing) {
            // Same title used by several templates — merge type usage
            existing.usedByIssues = existing.usedByIssues || Number(status.usedByIssues) > 0;
            existing.usedByProcesses = existing.usedByProcesses || Number(status.usedByProcesses) > 0;
          } else {
            uniqueStatuses.set(status.title, {
              title: status.title,
              color: status.color || '#999999',
              sort: Number(status.sort || 0),
              isDraft: Boolean(status.isDraft),
              usedByIssues: Number(status.usedByIssues) > 0,
              usedByProcesses: Number(status.usedByProcesses) > 0
            });
          }
        });
      allStatusTemplates.value = Array.from(uniqueStatuses.values());
    } catch (error) {
      console.error('Error loading status templates for filter:', error);
    }
  };

  // Statuses shown in the dialog: only those used by at least one selected item type
  const statusTemplates = computed(() =>
    allStatusTemplates.value.filter(status =>
      (localTypeFilters.value.issues && status.usedByIssues) ||
      (localTypeFilters.value.processes && status.usedByProcesses)
    )
  );

  // Check if all filters (statuses and types) are selected
  const allFiltersSelected = computed(() => {
    const filterValues = Object.values(localFilters.value);
    const typeValues = Object.values(localTypeFilters.value);
    return filterValues.length > 0 && filterValues.every(v => v === true) &&
           typeValues.every(v => v === true);
  });

  // Only visible statuses count: a deselected status hidden by the type
  // selection has no effect until its type is selected again
  const hasActiveStatusFilter = computed(() =>
    statusTemplates.value.some(status => localFilters.value[status.title] === false)
  );

  const hasActiveTypeFilter = computed(() => {
    const typeValues = Object.values(localTypeFilters.value);
    return typeValues.length > 0 && typeValues.some(v => v === false);
  });

  // Check if any filter is active (a status or an item type is deselected)
  const hasActiveFilter = computed(() => hasActiveStatusFilter.value || hasActiveTypeFilter.value);

  // Toggle individual filter
  const toggleStatus = (statusTitle) => {
    const newFilters = { ...localFilters.value };
    newFilters[statusTitle] = !newFilters[statusTitle];
    localFilters.value = newFilters;
    statusFilters.value = newFilters;
  };

  // Toggle individual item type filter
  const toggleType = (type) => {
    const newFilters = { ...localTypeFilters.value };
    newFilters[type] = !newFilters[type];
    localTypeFilters.value = newFilters;
    itemTypeFilters.value = newFilters;
  };

  // Toggle all filters (select all or deselect all), statuses and types together.
  // Acts on ALL statuses, not just the visible ones, so the result is predictable
  // regardless of the current type selection
  const toggleAll = async () => {
    const newValue = !allFiltersSelected.value;
    const newFilters = {};
    allStatusTemplates.value.forEach(status => {
      newFilters[status.title] = newValue;
    });
    const newTypeFilters = {};
    ITEM_TYPES.forEach(type => {
      newTypeFilters[type] = newValue;
    });
    localFilters.value = { ...newFilters };
    localTypeFilters.value = { ...newTypeFilters };
    statusFilters.value = newFilters;
    itemTypeFilters.value = newTypeFilters;
    await nextTick();
    await applyFilters();
  };

  const applyStatusFilter = async () => {
    // Filter on the visible statuses only (those of the selected item types) —
    // hidden statuses are already excluded by the intersecting type filter
    const visibleTitles = statusTemplates.value.map(status => status.title);
    const selectedStatuses = visibleTitles.filter(title => statusFilters.value[title]);
    const totalStatuses = visibleTitles.length;

    // If all statuses are selected, remove the status filter
    if (selectedStatuses.length === totalStatuses) {
      await itemsCtx().removeFilter(STATUS_ITEMS_ID);
      await historyCtx().removeFilter(STATUS_HISTORY_ID);
      return;
    }

    // If no statuses selected, show nothing
    if (selectedStatuses.length === 0) {
      await itemsCtx().updateFilter(STATUS_ITEMS_ID, []);
      await historyCtx().updateFilter(STATUS_HISTORY_ID, []);
      return;
    }

    // Build SQL query for partial selection
    const statusList = selectedStatuses.map(s => `'${s.replace(/'/g, "''")}'`).join(', ');

    const itemsQuery = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    INNER JOIN status_templates st ON i.StatusId = st.Id
    WHERE st.Title IN (${statusList})
  `;

    const statusHistoryQuery = `
    SELECT DISTINCT sht.subItemId, sht.validFrom, sht.statusId
    FROM status_history_temporal sht
    INNER JOIN status_templates st ON sht.statusId = st.Id
    WHERE st.Title IN (${statusList})
  `;

    await itemsCtx().updateFilterWithQuery(STATUS_ITEMS_ID, itemsQuery);
    await historyCtx().updateFilterWithQuery(STATUS_HISTORY_ID, statusHistoryQuery);
  };

  const applyTypeFilter = async () => {
    const selected = ITEM_TYPES.filter(type => itemTypeFilters.value[type]);

    // If all (or no configured) types are selected, remove the type filter
    if (selected.length === ITEM_TYPES.length || Object.keys(itemTypeFilters.value).length === 0) {
      await itemsCtx().removeFilter(TYPE_ITEMS_ID);
      await historyCtx().removeFilter(TYPE_HISTORY_ID);
      return;
    }

    // If no types selected, show nothing
    if (selected.length === 0) {
      await itemsCtx().updateFilter(TYPE_ITEMS_ID, []);
      await historyCtx().updateFilter(TYPE_HISTORY_ID, []);
      return;
    }

    // Exactly one type selected
    const predicate = selected[0] === 'issues'
      ? `i."Type" = 'IssueTemplate'`
      : `COALESCE(i."Type", '') != 'IssueTemplate'`;

    const itemsQuery = `
    SELECT DISTINCT i.SubItemId
    FROM items i
    WHERE ${predicate}
  `;

    // status_history_temporal has no type column, so go through items
    const statusHistoryQuery = `
    SELECT DISTINCT sht.subItemId, sht.validFrom, sht.statusId
    FROM status_history_temporal sht
    INNER JOIN items i ON sht.subItemId = i.SubItemId
    WHERE ${predicate}
  `;

    await itemsCtx().updateFilterWithQuery(TYPE_ITEMS_ID, itemsQuery);
    await historyCtx().updateFilterWithQuery(TYPE_HISTORY_ID, statusHistoryQuery);
  };

  const applyFilters = async () => {
    if (!unref(isInitialized) || !unref(dataLoaded)) {
      return;
    }

    if (!unref(conn)) return;

    try {
      await applyStatusFilter();
      await applyTypeFilter();
    } catch (error) {
      console.error('Error applying status filters:', error);
    }
  };

  // Watch for loaded templates to initialize filters (all selected by default)
  watch(allStatusTemplates, (newTemplates) => {
    if (newTemplates.length > 0) {
      if (Object.keys(statusFilters.value).length === 0) {
        const newFilters = {};
        newTemplates.forEach(status => {
          newFilters[status.title] = true;
        });
        statusFilters.value = newFilters;
        applyFilters();
      }
    }
  }, { immediate: true });

  // Watch for changes in filters and apply them in realtime
  watch(() => statusFilters.value, () => {
    applyFilters();
  }, { deep: true });

  watch(() => itemTypeFilters.value, () => {
    applyFilters();
  }, { deep: true });

  // Watch for data loaded to load templates and reapply existing filters
  watch(() => unref(dataLoaded), (isLoaded) => {
    if (isLoaded) {
      reload();
      if (Object.keys(statusFilters.value).length > 0) {
        applyFilters();
      }
    }
  }, { immediate: true });

  // Reapply when a filter context is re-initialized elsewhere (e.g. App.vue after
  // data load, or ActivityPage rebuilding status_history_temporal), which wipes
  // registered filters. Guarded on the filter being absent so reapplying (which
  // bumps filtersLastUpdated itself) cannot loop.
  //
  // These read `.filtersLastUpdated.value` directly (no `deep`) on purpose:
  // they are re-registration guards, not refreshes, so they must NOT go through
  // useFilterContextRefresh. `context.filters` is markRaw and therefore read
  // LIVE at the moment the watcher fires — that live read is the whole loop
  // guard (R3). Do not snapshot it.
  watch(() => itemsCtx().filtersLastUpdated.value, () => {
    if (!unref(dataLoaded)) return;
    const registered = itemsCtx().filters;
    if ((hasActiveStatusFilter.value && !(STATUS_ITEMS_ID in registered)) ||
        (hasActiveTypeFilter.value && !(TYPE_ITEMS_ID in registered))) {
      applyFilters();
    }
  });

  watch(() => historyCtx().filtersLastUpdated.value, () => {
    if (!unref(dataLoaded)) return;
    const registered = historyCtx().filters;
    if ((hasActiveStatusFilter.value && !(STATUS_HISTORY_ID in registered)) ||
        (hasActiveTypeFilter.value && !(TYPE_HISTORY_ID in registered))) {
      applyFilters();
    }
  });

  return {
    itemTypes: ITEM_TYPES,
    allStatusTemplates,
    statusTemplates,
    allFiltersSelected,
    hasActiveStatusFilter,
    hasActiveTypeFilter,
    hasActiveFilter,
    toggleStatus,
    toggleType,
    toggleAll,
    reload
  };
}
